import { motion } from "framer-motion";
import { HOUSE } from "@/constants/testIds";

const CLOSED_IMG = "/media/abike-closed.jpg";
const DEPLOYED_IMG = "/media/abike-deployed.jpg";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

export const Solution = () => {
  return (
    <section
      id="solution"
      data-testid={HOUSE.sectionSolution}
      className="relative bg-walnut-grain py-28 md:py-36 border-t border-[var(--border-dark)]"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-20">
          <motion.p
            {...fadeUp}
            className="font-sans text-[11px] uppercase tracking-[0.5em] text-[var(--gold-light)] mb-8"
          >
            The Abike Collection
          </motion.p>
          <motion.h2
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.08 }}
            className="font-serif text-[var(--ivory)] text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight font-light"
          >
            A quiet answer,
            <br />
            <span className="italic text-[var(--gold-light)]">built into the wall.</span>
          </motion.h2>
          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.16 }}
            className="mt-8 font-sans text-[var(--stone)] text-lg leading-relaxed max-w-xl"
          >
            Abike remains discreetly integrated into the environment until
            needed. A press deploys it to ninety degrees; a gesture returns it
            to the wall.
          </motion.p>
        </div>

        {/* Dual state — Closed / Deployed */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-10">
          <StateCard
            image={CLOSED_IMG}
            eyebrow="01 · Closed"
            title="Sleek. Architectural. Intentional."
            body="12 × 9 inches. 0.88-inch closed profile. A concealed torque hinge holds the platform flush to the wall — reading as considered architecture, not equipment."
            alt="Abike Collection closed — walnut door"
          />
          <StateCard
            image={DEPLOYED_IMG}
            eyebrow="02 · Deployed"
            title="Effortless. Elegant. Essential."
            body="A ninety-degree deployment. A recessed ivory surface. A temporary landing place for whatever the moment asks you to set down."
            alt="Abike Collection deployed — walnut restroom setting"
          />
        </div>

        {/* Spec strip */}
        <motion.div
          {...fadeUp}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--border-dark)] border border-[var(--border-dark)]"
        >
          {[
            ["12 × 9 in", "Platform dimensions"],
            ["0.88 in", "Closed depth"],
            ["90°", "Deployment"],
            ["Concealed", "Torque hinge"],
          ].map(([value, label]) => (
            <div
              key={label}
              className="bg-[var(--walnut-deep)] p-8 md:p-10"
            >
              <div className="font-serif text-[var(--gold-light)] text-2xl md:text-3xl tracking-tight">
                {value}
              </div>
              <div className="mt-3 font-sans text-[10px] uppercase tracking-[0.32em] text-[var(--stone)]/70">
                {label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

const StateCard = ({ image, eyebrow, title, body, alt }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
    className="group"
  >
    <div className="relative aspect-[4/5] md:aspect-[5/6] overflow-hidden bg-[var(--walnut-deep)]">
      <img
        src={image}
        alt={alt}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.03]"
      />
      <div className="absolute top-5 left-5 w-10 h-10 border-t border-l border-[var(--gold)]/40" />
      <div className="absolute bottom-5 right-5 w-10 h-10 border-b border-r border-[var(--gold)]/40" />
    </div>
    <div className="mt-8">
      <p className="font-sans text-[10px] uppercase tracking-[0.42em] text-[var(--gold-light)] mb-4">
        {eyebrow}
      </p>
      <h3 className="font-serif text-[var(--ivory)] text-2xl md:text-3xl leading-snug tracking-tight">
        {title}
      </h3>
      <p className="mt-4 font-sans text-[var(--stone)]/85 text-base leading-relaxed max-w-md">
        {body}
      </p>
    </div>
  </motion.div>
);
